import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Paper, Stack, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Switch, FormControlLabel, Grid, IconButton, Menu, MenuItem } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import apiService from '../services/api.service'

type Row = { id: number; companyid?: number | null; code: string; name: string; active?: boolean; data?: any }

const Projects: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<{ code: string; name: string; active: boolean }>({ code: '', name: '', active: true })
  const [fields, setFields] = useState<string[]>([])
  const [extras, setExtras] = useState<Record<string, any>>({})
  const [search, setSearch] = useState('')
  const [activeOnly, setActiveOnly] = useState(false)
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [rowMenuRow, setRowMenuRow] = useState<Row | null>(null)
  const rowMenuOpen = Boolean(rowMenuAnchorEl)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined
      const res = await apiService.listProjects(selectedCompanyId)
      const list = (res as any)?.data || res || []
      const mapped = list.map((p: any) => ({ id: p.id ?? 0, companyid: p.companyid ?? null, code: String(p.code || p.Code || ''), name: String(p.name || p.Name || ''), active: typeof p.active === 'boolean' ? p.active : undefined, data: p.data }))
      setRows(mapped)
      const union = new Set<string>()
      for (const r of mapped) {
        const d = r.data || {}
        Object.keys(d).forEach(k => union.add(k))
      }
      try {
        const resFields = await apiService.getScheduleFields('sap-projects', selectedCompanyId)
        const keys = (resFields as any) || []
        for (const k of keys) union.add(String(k))
      } catch {}
      const baseFiltered = Array.from(union).filter(k => !['ProjectCode','Code','Name','Active'].includes(k))
      setFields(baseFiltered)
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const onCompanyChanged = () => { void load(); };
    window.addEventListener('company-changed', onCompanyChanged as any);
    return () => { window.removeEventListener('company-changed', onCompanyChanged as any); };
  }, [load]);

  const columns: GridColDef[] = useMemo(() => {
    const base: GridColDef[] = [
      { field: 'code', headerName: 'Project Code', width: 180 },
      { field: 'name', headerName: 'Project Name', width: 240 },
      { field: 'active', headerName: 'Active', width: 120, valueFormatter: (p) => (p.value === undefined ? '' : (p.value ? 'Yes' : 'No')) },
    ]
    const dynamic: GridColDef[] = fields.map((f) => ({
      field: `data_${f}`,
      headerName: f,
      width: Math.max(140, Math.min(300, f.length * 9 + 40)),
      valueGetter: (params: any) => {
        const d = params.row?.data || {}
        return d[f]
      },
    }))
    const actions: GridColDef = {
      field: 'actions', headerName: 'Actions', width: 80, sortable: false, filterable: false, renderCell: (params) => (
        <IconButton size="small" aria-label="More actions" onClick={(e) => { setRowMenuAnchorEl(e.currentTarget); setRowMenuRow(params.row as Row); }}>
          <MoreVertIcon />
        </IconButton>
      )
    }
    return [...base, ...dynamic, actions]
  }, [fields])

  const openCreate = () => {
    setEditId(null);
    setForm({ code: '', name: '', active: true });
    const initialExtras: Record<string, any> = {}
    for (const k of fields) initialExtras[k] = ''
    setExtras(initialExtras)
    setOpen(true)
  }

  const save = async () => {
    try {
      if (editId) {
        const data: Record<string, any> = { ...extras }
        await apiService.updateProject(editId, { code: form.code, name: form.name, active: form.active, data })
      } else {
        const companyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined
        const data: Record<string, any> = { ...extras }
        await apiService.createProject({ companyid: companyId, code: form.code, name: form.name, active: form.active })
        if (Object.keys(data).length) {
          const createdRes = await apiService.listProjects(companyId)
          const list = (createdRes as any)?.data || createdRes || []
          const just = list.find((p: any) => String(p.code || p.Code || '') === form.code)
          if (just?.id) await apiService.updateProject(Number(just.id), { data })
        }
      }
      setOpen(false)
      await load()
    } catch {}
  }

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter(r => {
      const match = !q || String(r.code || '').toLowerCase().includes(q) || String(r.name || '').toLowerCase().includes(q)
      const act = !activeOnly || !!r.active
      return match && act
    })
  }, [rows, search, activeOnly])

  return (
    <Paper component="section" aria-labelledby="projects-heading" elevation={0} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={{ xs: 1.5, sm: 0 }}
        mb={1.5}
      >
        <Typography
          component="h1"
          id="projects-heading"
          variant="h6"
          sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700 }}
        >
          Project Management
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="contained" onClick={openCreate}>Add Project</Button>
          <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
        </Stack>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems="center" flexWrap="wrap" mb={1.5}>
        <TextField size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 200 }} inputProps={{ 'aria-label': 'Search projects' }} />
        <FormControlLabel control={<Switch checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />} label="Active Only" />
      </Stack>
      <DataGrid
        autoHeight
        rows={filteredRows}
        columns={columns}
        density="compact"
        pageSizeOptions={[50,100]}
        initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
        disableRowSelectionOnClick={false}
        sx={{ width: '100%' }}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editId ? 'Edit Project' : 'Add Project'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Project Code" fullWidth size="small" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Project Name" fullWidth size="small" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />} label="Active" />
              </Grid>
              {fields.map((k) => {
                const v = extras[k]
                const isBool = typeof v === 'boolean'
                return (
                  <Grid item xs={12} sm={6} key={k}>
                    {isBool ? (
                      <FormControlLabel control={<Switch checked={!!v} onChange={(e) => setExtras((ex) => ({ ...ex, [k]: e.target.checked }))} />} label={k} />
                    ) : (
                      <TextField label={k} fullWidth size="small" value={v ?? ''} onChange={(e) => setExtras((ex) => ({ ...ex, [k]: e.target.value }))} />
                    )}
                  </Grid>
                )
              })}
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!form.code.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
      <Menu anchorEl={rowMenuAnchorEl} open={rowMenuOpen} onClose={() => setRowMenuAnchorEl(null)}>
        <MenuItem onClick={() => {
          if (!rowMenuRow) return
          setEditId(Number(rowMenuRow.id));
          setForm({ code: String(rowMenuRow.code || ''), name: String(rowMenuRow.name || ''), active: !!rowMenuRow.active });
          const d = (rowMenuRow?.data || {}) as Record<string, any>
          const initialExtras: Record<string, any> = {}
          for (const k of fields) initialExtras[k] = d[k]
          setExtras(initialExtras)
          setOpen(true)
          setRowMenuAnchorEl(null)
        }}>Edit</MenuItem>
        <MenuItem onClick={async () => {
          if (!rowMenuRow) return
          const ok = window.confirm('Delete this project?')
          if (!ok) { setRowMenuAnchorEl(null); return }
          try { await apiService.deleteProject(Number(rowMenuRow.id)); await load() } catch {}
          setRowMenuAnchorEl(null)
        }}>Delete</MenuItem>
      </Menu>
    </Paper>
  )
}

export default Projects
